import {
  FetchPharmaStpFileDataParams,
  PharmaStpFileApiResponse,
} from "../../../api/bigQuery/pharmaStpFileOrders";
import { getBearerToken } from "../../../api/postCall/bearerToken";
import { API_ENDPOINTS } from "../../../enums/apiUrls";
import { OrganizationType } from "../../../enums/organizationTypes";
import { logDataToConsole, logError } from "../../../utils/customLogger";
import { NycbsExternalFilterPayload } from "../utils/nycbsExternalFilterPayload";

export type FetchNycbsExternalDataParams =
  | NycbsExternalFilterPayload
  | FetchPharmaStpFileDataParams;

/**
 * Fetches STP file rows for the NYCBS External table.
 * Scopes requests to the org_id in the payload (Non-STAT or STAT).
 *
 * @param params - Filter, pagination, sort, and org parameters for the request
 * @return PharmaStpFileApiResponse: Paginated STP file rows from BigQuery
 */
export const fetchNycbsExternalData = async (
  params: FetchNycbsExternalDataParams,
): Promise<PharmaStpFileApiResponse> => {
  const orgId =
    "org_id" in params && params.org_id
      ? params.org_id
      : OrganizationType.NYCBS_PHARMA;

  const body: Record<string, unknown> = {
    org_id: orgId,
    filters: params.filters ?? {},
    page: params.page ?? 1,
    request_id: params.request_id,
    order_by: params.order_by,
  };

  if (params.date_from_filename_start !== undefined) {
    body.date_from_filename_start = params.date_from_filename_start;
  }
  if (params.date_from_filename_end !== undefined) {
    body.date_from_filename_end = params.date_from_filename_end;
  }
  if (params.page_size !== undefined) {
    body.page_size = params.page_size;
  }

  if ("case_sensitive" in params && params.case_sensitive !== undefined) {
    body.case_sensitive = params.case_sensitive;
  }

  try {
    const token = await getBearerToken();
    const response = await fetch(API_ENDPOINTS.GET_PHARMA_STP_FILE_DATA, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const result = await response.json();
    logDataToConsole("fetchNycbsExternalData", result);
    return result;
  } catch (error) {
    logError(error as Error, "Error in fetchNycbsExternalData");
    throw error;
  }
};
